import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {scrollToSection} from 'components/ScrollSpy'
import {useGetActiveTabFromIntersectingSection} from 'hooks/useGetActiveTabFromIntersectingSection'
import {useTopHeaderHeight} from 'hooks/useTopHeaderHeight'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {
  getIsTopHeaderHidden,
  getSearchDesktopHeaderHeight
} from 'modules/search/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Icon} from '@vio/atlas/Icon'
import {useBodyScrollLockObserver} from '@vio/atlas/Overlay/useBodyScrollLock'
import {Tab as BaseTab, Tabs} from '@vio/atlas/Tabs'
import {cssTheme} from '@vio/atlas/themes'

import {useShouldShowHotelCardClickersContent} from '../AccommodationView/Desktop/useShouldShowHotelCardClickersContent'
import {HeaderCTA} from '../Offers/HeaderCTA'

export const ACCOMMODATION_FIXED_HEADER_HEIGHT = 70

type AccommodationFixedHeaderMode = 'inline' | 'sticky'

const StickyHeaderWrapper = styled.div<{
  scrollBarGap: number
  topOffset: number
}>`
  width: 100%;
  position: fixed;
  top: ${({topOffset}) => `${topOffset}px`};

  z-index: 100;
  background: ${cssTheme.colors.background.neutral.primary};
  border-bottom: 1px solid ${cssTheme.colors.border.neutral.secondary};
  &.--scroll-bar-gap {
    padding-right: ${({scrollBarGap}) => scrollBarGap}px;
  }
`

const InlineHeaderWrapper = styled.div`
  width: 100%;
  background: ${cssTheme.colors.background.neutral.primary};
  border-bottom: 1px solid ${cssTheme.colors.border.neutral.secondary};
`

const HeaderContentWrapper = styled.div`
  display: flex;
  align-items: center;
`

const TabsWrapper = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const OfferCtaWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const Tab = styled(BaseTab)`
  color: ${cssTheme.colors.content.neutral.secondary};
`

const handleScrollTop = () => window.scrollTo({top: 0, behavior: 'smooth'})

interface Props {
  hasReviews?: boolean
  hasFacilities?: boolean
  hasRooms?: boolean
  hasPolicies?: boolean
  isVisible?: boolean
  isCtaVisible?: boolean
  isLoading?: boolean
  isPriceTrendsEnabled?: boolean
  hasFAQs?: boolean
  mode?: AccommodationFixedHeaderMode
}

export const AccommodationFixedHeader: React.FC<Props> = ({
  hasReviews = true,
  hasFacilities = true,
  hasPolicies = true,
  hasRooms = true,
  isCtaVisible = false,
  isVisible,
  isLoading = false,
  isPriceTrendsEnabled = false,
  hasFAQs = false,
  mode = 'sticky'
}) => {
  const {isDesktopMd, isDesktopLg} = useDeviceLayout()
  const isSearchBoxSticky = isDesktopMd || isDesktopLg
  const searchDesktopHeaderHeight = useSelector(getSearchDesktopHeaderHeight)
  const topHeaderIsHidden = useSelector(getIsTopHeaderHidden)
  const {scrollBarGap} = useBodyScrollLockObserver()
  const topHeaderHeight = useTopHeaderHeight()
  const {formatMessage} = useIntl()
  const isInline = mode === 'inline'
  const shouldShowHotelCardClickersContent =
    useShouldShowHotelCardClickersContent()

  const stickyHeaderOffset = isSearchBoxSticky
    ? topHeaderIsHidden
      ? searchDesktopHeaderHeight - topHeaderHeight
      : searchDesktopHeaderHeight
    : 0

  const stickyHeaderHeight =
    ACCOMMODATION_FIXED_HEADER_HEIGHT + searchDesktopHeaderHeight

  const onChange = (tab: string) => {
    if (tab === AccommodationPageNavSections.Photos) {
      handleScrollTop()
    } else {
      scrollToSection(tab)
    }
  }

  const intersectionPoint = isSearchBoxSticky
    ? stickyHeaderHeight
    : ACCOMMODATION_FIXED_HEADER_HEIGHT

  const {activeTab: observedActiveTab} = useGetActiveTabFromIntersectingSection(
    {
      intersectWithSectionAt: intersectionPoint,
      shouldSkip: !isVisible || isInline
    }
  )

  const activeTab = isInline
    ? AccommodationPageNavSections.Overview
    : observedActiveTab

  if (!isVisible && !isInline) {
    return null
  }

  const tabs = shouldShowHotelCardClickersContent ? (
    <Tabs
      withDivider={false}
      activeTab={activeTab}
      mode="navigation"
      onTabClick={onChange}
    >
      <Tab
        label={formatMessage({id: 'overview', defaultMessage: 'Overview'})}
        value={AccommodationPageNavSections.Overview}
      />
      {hasRooms && (
        <Tab
          label={formatMessage({id: 'rooms', defaultMessage: 'Rooms'})}
          value={AccommodationPageNavSections.Rooms}
        />
      )}
      {hasReviews && (
        <Tab
          label={formatMessage({id: 'reviews', defaultMessage: 'Reviews'})}
          value={AccommodationPageNavSections.Reviews}
        />
      )}
      {hasPolicies && (
        <Tab
          label={formatMessage({
            id: 'propertyPolicy',
            defaultMessage: 'Property policies'
          })}
          value={AccommodationPageNavSections.Policies}
        />
      )}
      {hasFAQs && (
        <Tab
          label={formatMessage({id: 'faqs.title', defaultMessage: 'FAQs'})}
          value={AccommodationPageNavSections.FAQs}
        />
      )}
    </Tabs>
  ) : (
    <Tabs
      withDivider={false}
      activeTab={activeTab}
      mode="navigation"
      onTabClick={onChange}
    >
      <Tab
        label={formatMessage({id: 'photos', defaultMessage: 'Photos'})}
        value={AccommodationPageNavSections.Photos}
      />
      {hasFacilities && (
        <Tab
          label={formatMessage({
            id: 'amenities',
            defaultMessage: 'Amenities'
          })}
          value={AccommodationPageNavSections.Amenities}
        />
      )}

      {isPriceTrendsEnabled && (
        <Tab
          label={formatMessage({
            id: 'priceTrend',
            defaultMessage: 'Price Trend'
          })}
          icon={
            <Icon size="md" name="Sparkles" colorPath="content.brand.primary" />
          }
          value={AccommodationPageNavSections.PriceTrend}
        />
      )}

      {hasRooms && (
        <Tab
          label={formatMessage({id: 'rooms', defaultMessage: 'Rooms'})}
          value={AccommodationPageNavSections.Rooms}
        />
      )}

      {hasReviews && (
        <Tab
          label={formatMessage({id: 'reviews', defaultMessage: 'Reviews'})}
          value={AccommodationPageNavSections.Reviews}
        />
      )}

      <Tab
        label={formatMessage({id: 'location', defaultMessage: 'Location'})}
        value={AccommodationPageNavSections.HotelLocation}
      />

      {hasPolicies && (
        <Tab
          label={formatMessage({
            id: 'propertyPolicy',
            defaultMessage: 'Property policies'
          })}
          value={AccommodationPageNavSections.Policies}
        />
      )}
      {hasFAQs && (
        <Tab
          label={formatMessage({id: 'faqs.title', defaultMessage: 'FAQs'})}
          value={AccommodationPageNavSections.FAQs}
        />
      )}
    </Tabs>
  )

  const cta = (
    <OfferCtaWrapper>
      <HeaderCTA isLoading={isLoading} isVisible={isCtaVisible} />
    </OfferCtaWrapper>
  )

  const headerContent = (
    <HeaderContentWrapper>
      <TabsWrapper>{tabs}</TabsWrapper>
      {cta}
    </HeaderContentWrapper>
  )

  if (isInline) {
    // Inline mode is rendered inside the page's existing PageWidthContainer,
    // so skip the inner one to keep the first tab flush with gallery/deals.
    // Hide it without removing it: dropping its height would shift everything
    // below it while a smooth scroll is on its way there.
    return (
      <InlineHeaderWrapper
        style={isVisible ? undefined : {visibility: 'hidden'}}
      >
        {headerContent}
      </InlineHeaderWrapper>
    )
  }

  return (
    <StickyHeaderWrapper
      className={cx(scrollBarGap > 0 && '--scroll-bar-gap')}
      scrollBarGap={scrollBarGap}
      topOffset={stickyHeaderOffset}
    >
      <PageWidthContainer paddingDesktopXsSize="s500">
        {headerContent}
      </PageWidthContainer>
    </StickyHeaderWrapper>
  )
}
